import { Icon as ConstellationIcon } from '@zillow/constellation-10';
import type { ComponentProps, ComponentType, SVGProps } from 'react';
import type { LucideIcon as LucideIconType } from 'lucide-react';
import { token } from '@/styled-system/tokens';

// Accept either a real lucide-react icon (a ForwardRefExoticComponent — what
// `import { Home } from 'lucide-react'` resolves to) or a plain SVG component
// for the handful of bespoke icons under src/components/ui/icons/.
export type LucideIconComponent = LucideIconType | ComponentType<SVGProps<SVGSVGElement>>;

export type LucideIconProps = Omit<ComponentProps<typeof ConstellationIcon>, 'children' | 'render'> & {
  icon: LucideIconComponent;
  /**
   * Stroke color for Lucide-style stroke icons. Accepts any CSS color string
   * (raw value, CSS var, `token.var('colors.*')`). Defaults to `currentColor`.
   */
  color?: string;
  /**
   * Fill for the SVG path. Defaults to `none` because Lucide icons are
   * stroke-first; pass `currentColor` for saved/selected filled states.
   */
  fill?: string;
  /**
   * When true, renders the raw SVG element directly — no Constellation `<Icon>`
   * wrapper. Use this when passing LucideIcon as the `render` prop of a
   * Constellation component (e.g. `<Menu.ItemIcon render={<LucideIcon asRender />}`),
   * where both components are `<Icon>` under the hood and nesting them would
   * produce conflicting size styles. The parent's Slot lands directly on the SVG.
   */
  asRender?: boolean;
};

/**
 * Renders a Lucide icon via Constellation's `<Icon>` slot wrapper. Forwards
 * Constellation Icon props (`size`, `css`, etc.) while handling `title`
 * locally: pass `title` for an accessible name (`aria-label` plus a plain SVG
 * `<title>`); omit it for a decorative icon (`aria-hidden`).
 *
 * Three HotPads overrides applied via inline style (so they win over CSS rules):
 *  - `fill: none` — Constellation's icon recipe applies `fill: currentColor`,
 *    which would otherwise collapse Lucide stroke icons (open chevrons,
 *    message bubbles) into filled silhouettes.
 *  - `stroke: <color>` — Lucide stroke icons read the SVG `stroke` attribute
 *    (defaulting to `currentColor`). The `color` prop maps directly to it so
 *    callers can paint icons with `token.var('colors.brand.primary')` etc.
 *  - `strokeWidth: 2` — Lucide's default. Pinned via inline style to survive
 *    being slotted into Constellation primitives.
 *  - `strokeLinecap/strokeLinejoin: round` — also Lucide defaults, but only as
 *    SVG presentation attributes, which lose to the Panda preflight's scoped
 *    `.c11n { all: unset }` once Constellation's Icon slots that class onto
 *    the SVG. Without the round caps, zero-length "dot" strokes (Smile's
 *    eyes, ellipsis dots) vanish entirely.
 *
 * For Constellation's own filled brand glyphs use `<Icon>` from
 * `@zillow/constellation-10` directly — this wrapper is Lucide-shaped.
 *
 * Pass `asRender` when using as the `render` prop of another Constellation
 * `<Icon>`-based component to avoid double-Icon nesting.
 */
export function LucideIcon({
  icon: IconComponent,
  color = 'currentColor',
  fill = 'none',
  style,
  title,
  asRender = false,
  ...rest
}: LucideIconProps) {
  const titleProps = title
    ? {
        'aria-label': rest['aria-label'] ?? title,
        'aria-hidden': 'false' as const,
      }
    : undefined;
  const icon = title ? (
    <IconComponent>
      <title>{title}</title>
    </IconComponent>
  ) : (
    <IconComponent />
  );

  if (asRender) {
    // Drop Constellation-only props (size, css) that are invalid on raw SVG elements.
    // Everything else (className, role, aria-*, data-*, focusable) is forwarded so the
    // parent's Slot can land its grid-area/sizing styles on the SVG.
    // Size is resolved as an inline style so it beats any class-based size set by the parent recipe.
    const { size, css: _css, ...svgProps } = rest as typeof rest & { size?: string; css?: unknown };
    const sizeStyle = size
      ? {
          width: token.var(`sizes.icon.${size}` as Parameters<typeof token.var>[0]),
          height: token.var(`sizes.icon.${size}` as Parameters<typeof token.var>[0]),
        }
      : undefined;
    return (
      <IconComponent
        style={{
          fill,
          stroke: color,
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          ...sizeStyle,
          ...style,
        }}
        {...svgProps}
        {...titleProps}
      >
        {title && <title>{title}</title>}
      </IconComponent>
    );
  }

  return (
    <ConstellationIcon
      style={{ fill, stroke: color, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', ...style }}
      {...rest}
      {...titleProps}
    >
      {icon}
    </ConstellationIcon>
  );
}
